import {useStore} from 'vuex';
import {computed} from 'vue';
import {default as dayjs} from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {pluralize} from '@/utils/text';
import {useCompany} from '@/composables/useCompany.ts';
import type {SubscriptionPlan} from '@/types/subscription';
import {CENTS_TO_DOLLARS} from '@/constants/money.ts';

dayjs.extend(isSameOrBefore);

export interface planSupportedFeatureCounts {
    listings: number | null;
    locations: number | null;
    carSharingConnections: number | null;
    smartCarConnections: number | null;
    orders: number | null;
    seats: number | null;
}

type ConnectionKey =
    | 'tesla'
    | 'bouncie'
    | 'turo'
    | 'getaround'
    | 'ryde';

export type PlanFeatureKey =
    | 'api'
    | 'esign'
    | 'smartPricing'
    | 'insurance'
    | 'whateverElse'
    | 'listing_exports'
    | 'webhooks'
    | 'coupons'
    | 'custom_tos'
    | 'barcodes'
    | 'custom_domain'
    | 'custom_checkout_fields'
    | 'secondary_driver'
    | 'additional_scripts'
    | 'seats';

const UNLIMITED_FEATURE_COUNT = -1;

type FeatureCountKey = keyof planSupportedFeatureCounts;


export function useSubscription() {
    const store = useStore();
    const {company, companyCounts} = useCompany();

    const enforceSubscription = computed(() => store.getters['subscriptions/enforceSubscription']);
    const customersLoading = computed(() => store.getters['renters/rentersLoading']);

    const hasActiveSubscription = computed(() => store.getters['subscriptions/hasActiveSubscription']);
    const subscriptionPlans = computed<SubscriptionPlan[]>(() => store.getters['subscriptions/plans']);
    const subscriptionPlan = computed(() => store.getters['subscriptions/subscriptionPlan']);
    const subscription = computed(() => store.getters['subscriptions/subscription']);
    const user = computed(() => store.getters['auth/user']);
    const stripeSubscriptionObject = computed(() => store.getters['stripe/stripeSubscriptionObject']);
    const subscriptionsLoading = computed(() => store.getters['subscriptions/loading']);
    const subscriptionEndDate = computed(() => store.getters['subscriptions/subscriptionEndDate']);
    const hasPastDueSubscription = computed(() => store.getters['subscriptions/hasPastDueSubscription']);
    const hasPausedSubscription = computed(() => store.getters['subscriptions/hasPausedSubscription']);

    const stripeTrialDate = computed(() => subscription.value?.trial_ends_at);
    const planFeatureCountsFromStore = computed(() => store.getters['subscriptions/planFeatureCounts'] || {});
    const planFeatureFlagsFromStore = computed(() => store.getters['subscriptions/planFeatureFlags'] || {});
    const extraListingsAddonCount = computed<number>(() =>
        store.getters['subscriptions/extraListingsAddonCount'] ?? 0
    );

    const resolveFeatureCount = (
        feature: keyof planSupportedFeatureCounts
    ): number | null => {
        const plan = subscriptionPlan.value;

        if (!plan) {
            return null;
        }

        const value = planFeatureCountsFromStore.value?.[feature]?.[plan];

        return typeof value === 'number'
            ? value
            : null;
    };

    const resolveListingFeatureCount = (): number | null => {
        const baseListingCount = resolveFeatureCount('listings');

        if (baseListingCount === null || baseListingCount === UNLIMITED_FEATURE_COUNT) {
            return baseListingCount;
        }

        return baseListingCount + extraListingsAddonCount.value;
    };

    const planSupportedFeatureCounts = computed<planSupportedFeatureCounts>(() => ({
        listings: resolveListingFeatureCount(),
        locations: resolveFeatureCount('locations'),
        carSharingConnections: resolveFeatureCount('carSharingConnections'),
        smartCarConnections: resolveFeatureCount('smartCarConnections'),
        orders: resolveFeatureCount('orders'),
        seats: resolveFeatureCount('seats'),
    }));

    const isUnlimitedFeatureCount = (value: number | null): boolean => {
        return value === UNLIMITED_FEATURE_COUNT;
    };

    const subscriptionLimitsDisabled = computed(() => {
        return !enforceSubscription.value || (inTrial.value && !hasActiveSubscription.value);
    });

    const isFeatureLimitReached = (current: number, feature: FeatureCountKey): boolean => {
        if (subscriptionLimitsDisabled.value) return false;
        if (!hasActiveSubscription.value) return true;

        const limit = planSupportedFeatureCounts.value[feature];

        if (limit === null) return true;
        if (isUnlimitedFeatureCount(limit)) return false;

        return current >= limit;
    };

    const formatFeatureLimit = (limit: number | null): string => {
        if (limit === null) return 'your current plan';
        if (isUnlimitedFeatureCount(limit)) return 'unlimited';

        return `<b>${limit.toLocaleString()}</b>`;
    };

    const getLimitReachedMessage = (feature: FeatureCountKey, label: string): string => {
        if (!enforceSubscription.value) {
            return `You need a subscription plan to add ${label.toLowerCase()}.`;
        }

        const limit = planSupportedFeatureCounts.value[feature];

        if (limit === null) {
            return `Your current plan does not support additional ${label.toLowerCase()}.`;
        }

        if (isUnlimitedFeatureCount(limit)) {
            return `Your plan includes unlimited ${label.toLowerCase()}.`;
        }

        return `You've reached your max ${label.toLowerCase()} of ${formatFeatureLimit(limit)} for your plan.`;
    };

    const countConnections = (
        keys: ConnectionKey[]
    ): number => {
        return keys.reduce((total, key) => {
            return total + (
                company.value?.connections?.[key]?.length ?? 0
            );
        }, 0);
    };


    const subscriptionStatus = computed(() => {
        const stripeStatus = subscription.value?.stripe_status;

        if (stripeStatus) return stripeStatus;

        if (user.value?.on_trial) return 'trialing';

        return 'inactive';
    });

    const trialEndDateRaw = computed(() => {
        return company.value?.trial_ends_at ?? stripeTrialDate.value;
    });

    const cancelSubscription = async () => {
        return store.dispatch('subscriptions/cancelSubscription');
    }

    const cancelSubscriptionStripePortal = async () => {
        return store.dispatch('subscriptions/cancelSubscriptionStripePortalUrl');
    }

    const endTrial = async () => {
        return store.dispatch('subscriptions/endTrial');
    }

    const switchPlan = async (plan: string) => {
        return store.dispatch('subscriptions/switchPlan', plan);
    }

    const resumeSubscription = async () => {
        return store.dispatch('subscriptions/resumeSubscription');
    }

    const addSubscriptionAddon = async (payload: { id: string, count: number }) => {
        return store.dispatch('subscriptions/addAddon', payload);
    }
    const removeSubscriptionAddon = async (addon: string | { id: string, count?: number }) => {
        return store.dispatch('subscriptions/removeAddon', addon);
    }

    const trialEndsMessage = computed(() => {
        const rawDate = trialEndDateRaw.value;

        // Check if rawDate is valid
        if (!rawDate || !dayjs(rawDate).isValid() || subscriptionStatus.value === 'active') {
            return 'Trial has ended';
        }

        const trialDate = dayjs(rawDate);
        const trialDateFmt = trialDate.format('MMMM D, YYYY');

        // Determine if the trial has already ended
        const status = trialDate.isSameOrBefore(dayjs()) ? 'ended' : 'ends';

        return `Trial ${status} ${trialDateFmt}`;
    });

    const stripeEndDate = computed(() => {
        const ends_at = subscription.value?.ends_at;
        if (!ends_at) return;
        return dayjs(ends_at).format('MMMM D, YYYY');
    });

    const inGracePeriod = computed(() => {
        const endDate = subscription.value?.ends_at;
        const status = subscriptionStatus.value;

        if (!endDate) return false;

        return !(status === 'canceled' && dayjs(endDate).diff(dayjs()) <= 0);
    });


    const inTrial = computed((): boolean => {
        const trialDate = user?.value?.on_trial;

        if (trialDate) return true;

        if (subscriptionStatus.value === 'trialing') return true;
        if (subscriptionStatus.value === 'active') return false;

        if (stripeTrialDate.value) {
            return dayjs(stripeTrialDate.value).diff(dayjs()) > 0;
        }

        return false;
    });

    const isTrialing = computed((): boolean => {
        return subscriptionStatus.value === 'trialing';
    })

    const trialEndDate = computed(() => {

        if (!trialEndDateRaw.value) {
            return 'N/A';
        }

        return dayjs(trialEndDateRaw.value).format('MMMM D, YYYY');
    });


    const currentStripeSubscriptionObjectRenewalDate = computed(() => {
        const date = stripeSubscriptionObject.value?.current_period_end;
        const status = stripeSubscriptionObject.value?.status;

        if (!date) return null;
        if (status === 'canceled') return null;

        return dayjs(date * 1000).format('MMMM D, YYYY');
    });

    const currentStripeSubscriptionObjectPlanAmount = computed(() => {
        const amount = stripeSubscriptionObject.value?.plan?.amount;

        if (!amount) return '$0';

        if (amount % 100 === 0) {
            return `$${(amount * CENTS_TO_DOLLARS).toFixed(0)}`;
        }

        return `$${(amount * CENTS_TO_DOLLARS).toFixed(2)}`;
    });

    const currentStripeSubscriptionObjectPlanInterval = computed(() => {
        return stripeSubscriptionObject.value?.plan?.interval ?? 'month';
    });

    const includesFeature = (feature: PlanFeatureKey): boolean => {
        if (!enforceSubscription.value) return true;

        if (inTrial.value && !hasActiveSubscription.value) {
            return true;
        }

        if (!hasActiveSubscription.value) {
            return false;
        }

        return planFeatureFlagsFromStore.value?.[
            subscriptionPlan.value
            ]?.[feature] ?? false;
    };

    const includedInSubscription = includesFeature;

    const planIncludesApi = computed(() =>
        includesFeature('api')
    );

    const planIncludesESign = computed(() =>
        includesFeature('esign')
    );

    const trialDaysLeft = computed((): number => {

        if (!trialEndDateRaw.value) {
            return 0;
        }

        const daysLeft = dayjs(trialEndDateRaw.value).diff(dayjs(), 'days', true);
        return daysLeft > 0 ? Math.ceil(daysLeft) : 0; // Round up
    })

    const trialTotalDays = computed((): number => {
        return company.value?.trial_total_days || 14;
    });

    const trialDaysLeftPercent = computed((): number => {
        if (trialDaysLeft.value === 0) {
            return 0;
        }
        return Math.min(100, Math.round((trialDaysLeft.value / trialTotalDays.value) * 100));
    });

    const trialDaysLeftMessage = computed((): string => {
        if (hasActiveSubscription.value) {
            const plan = subscriptionPlan.value
                ? `${subscriptionPlan.value.charAt(0).toUpperCase()}${subscriptionPlan.value.slice(1)}`
                : 'Unknown';
            return `Membership: ${plan} Plan`;
        }
        if (trialDaysLeft.value <= 0) {
            return 'Your trial has ended';
        }
        return `You have ${trialDaysLeft.value} ${pluralize(trialDaysLeft.value, 'day')} left on your trial.`;
    })

    const trialDaysClass = computed(() => ({
        'text-danger': trialDaysLeft.value === 0,
        'text-warning': trialDaysLeft.value < 2,
    }));

    const currentPlanDetails = computed(() => {
        return subscriptionPlans.value.find((p) => p.name === subscriptionPlan.value);
    });

    const currentPlanBullets = computed(() => {
        return currentPlanDetails.value?.description.bullets;
    });

    const seatLimitReached = computed(() =>
        isFeatureLimitReached(companyCounts.value?.employees ?? 0, 'seats')
    );

    const seatLimitReachedMessage = computed(() =>
        getLimitReachedMessage('seats', 'seats')
    );

    const companyListings = computed(() => {
        return companyCounts.value?.listings;
    });

    const listingLimitReachedMessage = computed(() =>
        getLimitReachedMessage('listings', 'listings')
    );

    const listingLimitReached = computed(() =>
        isFeatureLimitReached(companyCounts.value?.listings ?? 0, 'listings')
    );


    const smartCarConnectionsLimitReached = computed(() =>
        isFeatureLimitReached(countConnections(['tesla', 'bouncie']), 'smartCarConnections')
    );

    const carShareConnectionsLimitReached = computed(() =>
        isFeatureLimitReached(countConnections(['turo', 'getaround', 'ryde']), 'carSharingConnections')
    );

    const carShareConnectionsLimitReachedMessage = computed(() =>
        getLimitReachedMessage('carSharingConnections', 'car-sharing connections')
    );

    return {
        subscription,
        seatLimitReached,
        seatLimitReachedMessage,
        subscriptionEndDate,
        currentPlanBullets,
        subscriptionsLoading,
        hasActiveSubscription,
        hasPausedSubscription,
        subscriptionStatus,
        subscriptionPlan,
        subscriptionPlans,
        enforceSubscription,
        customersLoading,
        inTrial,
        isTrialing,
        planIncludesApi,
        planIncludesESign,
        trialEndDate,
        currentStripeSubscriptionObjectRenewalDate,
        currentStripeSubscriptionObjectPlanAmount,
        currentStripeSubscriptionObjectPlanInterval,
        stripeEndDate,
        inGracePeriod,
        trialDaysLeftMessage,
        trialDaysLeft,
        trialDaysLeftPercent,
        trialDaysClass,
        trialEndsMessage,
        companyListings,
        listingLimitReachedMessage,
        listingLimitReached,
        hasPastDueSubscription,
        planSupportedFeatureCounts,
        currentPlanDetails,
        smartCarConnectionsLimitReached,
        carShareConnectionsLimitReached,
        carShareConnectionsLimitReachedMessage,
        addSubscriptionAddon,
        removeSubscriptionAddon,
        resumeSubscription,
        endTrial,
        switchPlan,
        cancelSubscription,
        cancelSubscriptionStripePortal,
        includedInSubscription,
    }
}
